import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  Users, 
  FileText, 
  Briefcase,
  Plus,
  Minus,
  Loader2,
  ChevronLeft,
  ChevronRight,
  History,
  AlertCircle
} from 'lucide-react';

// Leave type icons mapping
const leaveTypeIcons = {
  ANNUAL_LEAVE: Calendar,
  PATERNITY_LEAVE: Users,
  KLEIN_VERLET: FileText,
  UNPAID_LEAVE: Briefcase
};

// Leave type colors
const leaveTypeColors = {
  ANNUAL_LEAVE: 'bg-emerald-600',
  PATERNITY_LEAVE: 'bg-blue-600',
  KLEIN_VERLET: 'bg-amber-500',
  UNPAID_LEAVE: 'bg-slate-600'
};

export const UserBalances = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  
  const [targetUser, setTargetUser] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Adjustment dialog
  const [adjustDialog, setAdjustDialog] = useState({ open: false, balance: null });
  const [adjustmentType, setAdjustmentType] = useState('add');
  const [adjustmentDays, setAdjustmentDays] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchUserBalances();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedYear]);

  const fetchUserBalances = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/users/${userId}/balances?year=${selectedYear}`);
      if (response.data.success) {
        setTargetUser({
          id: userId,
          full_name: response.data.data.user_name
        });
        setBalances(response.data.data.balances);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch balances');
      if (error.response?.status === 404) {
        navigate('/dashboard/users');
      }
    } finally {
      setLoading(false);
    }
  };

  const openAdjustDialog = (balance) => {
    setAdjustDialog({ open: true, balance });
    setAdjustmentType('add');
    setAdjustmentDays('');
    setAdjustmentReason('');
  };

  const closeAdjustDialog = () => {
    setAdjustDialog({ open: false, balance: null });
    setAdjustmentDays('');
    setAdjustmentReason('');
  };

  const handleAdjustment = async () => {
    if (!adjustmentDays || !adjustmentReason.trim()) {
      toast.error(t('validation.required'));
      return;
    }

    const days = parseInt(adjustmentDays);
    if (isNaN(days) || days <= 0) {
      toast.error('Enter a valid number of days');
      return;
    }

    setProcessing(true);
    try {
      const delta = adjustmentType === 'add' ? days : -days;
      await api.post(`/admin/users/${userId}/balances/adjust`, {
        year: selectedYear,
        leave_type_code: adjustDialog.balance.leave_type_code,
        delta_days: delta,
        reason: adjustmentReason.trim()
      });
      
      toast.success(t('common.success'));
      closeAdjustDialog();
      fetchUserBalances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust balance');
    } finally {
      setProcessing(false);
    }
  };

  const BalanceCard = ({ balance }) => {
    const Icon = leaveTypeIcons[balance.leave_type_code] || Calendar;
    const colorClass = leaveTypeColors[balance.leave_type_code] || 'bg-slate-600';

    return (
      <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${colorClass}`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">{balance.name}</h3>
              {balance.requires_document && (
                <Badge variant="outline" className="text-xs text-amber-600 border-amber-200">
                  {t('leave.requiresDocument')}
                </Badge>
              )}
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => openAdjustDialog(balance)}
            data-testid={`adjust-btn-${balance.leave_type_code}`}
          >
            {t('leave.adjustBalance')}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 bg-emerald-50 rounded-lg">
            <p className="text-xl font-bold text-emerald-700">{balance.remaining_days}</p>
            <p className="text-xs text-emerald-600 uppercase">{t('leave.remaining')}</p>
          </div>
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-xl font-bold text-slate-600 dark:text-slate-300">{balance.used_days}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">{t('leave.used')}</p>
          </div>
          <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
            <p className="text-xl font-bold text-slate-600 dark:text-slate-300">
              {balance.entitled_days + (balance.adjustments_total || 0)}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">{t('leave.entitled')}</p>
          </div>
        </div>

        {/* Adjustment History */}
        {balance.adjustments && balance.adjustments.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <History className="h-4 w-4 text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{t('leave.adjustmentHistory')}</span>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {balance.adjustments.map((adj, idx) => (
                <div 
                  key={adj.id || idx} 
                  className="flex items-center justify-between text-sm p-2 bg-slate-50 dark:bg-slate-800 rounded"
                >
                  <div className="flex items-center gap-2">
                    {adj.delta_days > 0 ? (
                      <Plus className="h-3 w-3 text-emerald-600" />
                    ) : (
                      <Minus className="h-3 w-3 text-red-600" />
                    )}
                    <span className={adj.delta_days > 0 ? 'text-emerald-600' : 'text-red-600'}>
                      {adj.delta_days > 0 ? '+' : ''}{adj.delta_days} {t('leave.days')}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[150px]" title={adj.reason}>
                    {adj.reason}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-balances-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/dashboard/users')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('leave.balances')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">{targetUser?.full_name}</p>
        </div>

        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedYear(selectedYear - 1)}
            disabled={selectedYear <= 2020}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-md min-w-[80px] text-center font-medium">
            {selectedYear}
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setSelectedYear(selectedYear + 1)}
            disabled={selectedYear >= new Date().getFullYear() + 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Balances Grid */}
      {balances.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-8 text-center">
          <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 dark:text-slate-400">{t('leave.noBalances')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {balances.map((balance) => (
            <BalanceCard key={balance.leave_type_code} balance={balance} />
          ))}
        </div>
      )}

      {/* Adjustment Dialog */}
      <Dialog open={adjustDialog.open} onOpenChange={(open) => !open && closeAdjustDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {adjustDialog.balance && (
                <>
                  {(() => {
                    const Icon = leaveTypeIcons[adjustDialog.balance.leave_type_code] || Calendar;
                    return <Icon className="h-5 w-5 text-slate-600 dark:text-slate-300" />;
                  })()}
                  {t('leave.adjustBalance')}
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {adjustDialog.balance?.name} - {selectedYear}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Balance Info */}
            <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Current {t('leave.remaining')}:</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                {adjustDialog.balance?.remaining_days} {t('leave.days')}
              </span>
            </div>

            {/* Adjustment Type */}
            <div className="space-y-2">
              <Label>{t('leave.adjustBalance')}</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={adjustmentType === 'add' ? 'default' : 'outline'}
                  className={`flex-1 gap-2 ${adjustmentType === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                  onClick={() => setAdjustmentType('add')}
                >
                  <Plus className="h-4 w-4" />
                  {t('leave.addDays')}
                </Button>
                <Button
                  type="button"
                  variant={adjustmentType === 'remove' ? 'default' : 'outline'}
                  className={`flex-1 gap-2 ${adjustmentType === 'remove' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                  onClick={() => setAdjustmentType('remove')}
                >
                  <Minus className="h-4 w-4" />
                  {t('leave.removeDays')}
                </Button>
              </div>
            </div>

            {/* Days Input */}
            <div className="space-y-2">
              <Label>{t('leave.days')}</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={adjustmentDays}
                onChange={(e) => setAdjustmentDays(e.target.value)}
                placeholder="Enter number of days"
                data-testid="adjustment-days-input"
              />
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>{t('leave.adjustmentReason')} <span className="text-red-500">*</span></Label>
              <Textarea
                value={adjustmentReason}
                onChange={(e) => setAdjustmentReason(e.target.value)}
                placeholder="Enter reason for adjustment..."
                rows={3}
                data-testid="adjustment-reason-input"
              />
            </div>

            {/* Preview */}
            {adjustmentDays && (
              <div className={`p-3 rounded-lg flex items-center gap-2 ${adjustmentType === 'add' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">
                  New balance will be: {adjustDialog.balance?.remaining_days + (adjustmentType === 'add' ? parseInt(adjustmentDays) || 0 : -(parseInt(adjustmentDays) || 0))} {t('leave.days')}
                </span>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeAdjustDialog}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAdjustment}
              disabled={processing || !adjustmentDays || !adjustmentReason.trim()}
              className={adjustmentType === 'add' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-adjustment-btn"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserBalances;
